/**
 * 3DS Challenge Modal Component
 * Displays 3DS challenge iframe when challenge is required
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

interface ThreeDSChallengeModalProps {
  url: string;
  creq: string;
  transactionId: string;
  onChallengeComplete: (transactionId: string) => void;
  onClose: () => void;
}

export const ThreeDSChallengeModal: React.FC<ThreeDSChallengeModalProps> = ({
  url,
  creq,
  transactionId,
  onChallengeComplete,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Listen for challenge completion message from iframe
   * The message can come from various origins (challenge provider, Coinflow, etc.)
   */
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Accept messages from various origins (challenge providers, Coinflow, etc.)
      // We validate the message content instead of origin for flexibility
      const messageData = typeof event.data === 'string' ? event.data : event.data?.type || event.data;

      // Challenge completed successfully
      if (
        messageData === 'challenge_success' ||
        event.data?.type === 'challenge_success' ||
        (typeof event.data === 'object' && event.data?.status === 'success')
      ) {
        setIsLoading(false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        onChallengeComplete(transactionId);
      }

      // Challenge failed or cancelled
      if (
        messageData === 'challenge_failure' ||
        messageData === 'challenge_cancelled' ||
        event.data?.type === 'challenge_failure' ||
        event.data?.type === 'challenge_cancelled' ||
        (typeof event.data === 'object' && event.data?.status === 'failure')
      ) {
        setIsLoading(false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        onClose();
      }
    },
    [transactionId, onChallengeComplete, onClose],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleMessage]);

  // Listen for iframe load events
  // Hide spinner after challenge form loads so user can interact with it
  // But don't call onChallengeComplete - that only happens on success message
  const handleIframeLoad = useCallback(() => {
    // Wait for the challenge provider to fully load after form auto-submits
    // The form submits immediately, then redirects to challenge provider
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false); // Hide spinner so user can see and interact with challenge
      // Note: We do NOT call onChallengeComplete here - only on success message
    }, 3000); // 3 second delay to allow challenge provider to fully load
  }, []);

  /**
   * Generate iframe content with auto-submitting form
   */
  const iframeContent = `
    <html>
      <head>
        <title>3DS Challenge</title>
        <style>
          body {
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: transparent;
          }
          .loading {
            text-align: center;
            font-family: Arial, sans-serif;
            color: #666;
          }
        </style>
      </head>
      <body onload="document.challenge.submit()">
        <div class="loading">Loading verification...</div>
        <form method="post" name="challenge" action="${url}">
          ${creq ? `<input type="hidden" name="creq" value="${creq.replace(/"/g, '&quot;')}" />` : ''}
        </form>
      </body>
    </html>
  `;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] max-h-[100vh] w-full h-full m-0 p-0 rounded-none border-0 bg-background overflow-hidden left-0 top-0 translate-x-0 translate-y-0 flex flex-col [&>button]:hidden pt-[env(safe-area-inset-top)] lg:pt-0 pb-[env(safe-area-inset-bottom)] lg:pb-0">
        <DialogHeader className="pb-2 p-2.5 lg:p-6 sticky top-0 bg-gradient-to-b from-background via-background/95 to-background/90 border-b border-border backdrop-blur-md z-10 relative">
          <div className="flex items-center justify-center gap-2 relative">
            <DialogTitle className="text-sm lg:text-2xl font-black text-foreground text-center">
              🔒 Complete Verification
            </DialogTitle>
            {/* Close button - Desktop only */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute right-0 top-0 h-8 w-8 lg:h-10 lg:w-10 rounded-full hover:bg-muted/80 flex-shrink-0 hidden lg:flex"
              aria-label="Close"
            >
              <X className="h-4 w-4 lg:h-5 lg:w-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="relative flex-1 min-h-0" style={{ minHeight: 'calc(100vh - 80px)' }}>
          {/* Loading overlay - only show while initial load, hide once iframe is ready */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/95 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 lg:h-8 lg:w-8 border-2 border-primary border-t-transparent mx-auto mb-2 lg:mb-3"></div>
                <p className="text-[10px] lg:text-sm text-muted-foreground font-medium">
                  Loading verification...
                </p>
              </div>
            </div>
          )}

          {/* 3DS Challenge iframe */}
          <iframe
            ref={iframeRef}
            onLoad={handleIframeLoad}
            style={{
              width: '100%',
              height: '100%',
              minHeight: 'calc(100vh - 80px)',
              border: 'none',
              display: 'block',
            }}
            srcDoc={iframeContent}
            title="3DS Challenge"
            sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation"
            allow="payment"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

